import { useHistory, useLocation, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import useRoutes from '../../../../hooks/useRoutes';
import { useTranslate } from '../../../../hooks/context/useTranslate';
import { useDebouncedEffect } from '../../../../hooks/useDebouncedEffect';
import { GlobalLocationStateType } from '../../../../types/types';
import { DETAIL_ROW_SYNC_DEBOUNCE_MS } from '../../../../constants';

interface UseSyncDetailRowToDataArgs {
  rows: any[];
  dataKey: string;
  expandedRow: any;
  isDatasetHookLoading: boolean;
  setExpandedRow: (row: any) => void;
  setCurrentSelectedRowIndex: (index: number) => void;
  buildDetailUrlForRow: (newItemId: string) => string;
}

// Keeps the displayed row, its paginator index and the URL pointing at the same
// record after the data changes. Runs once the data settles: re-finds the
// expanded row, or picks the row the URL asks for, or falls back to the first
// row with a toast when that row is not in the data.
export const useSyncDetailRowToData = ({
  rows,
  dataKey,
  expandedRow,
  isDatasetHookLoading,
  setExpandedRow,
  setCurrentSelectedRowIndex,
  buildDetailUrlForRow,
}: UseSyncDetailRowToDataArgs): void => {
  const { datasetId, itemId } = useParams<{
    datasetId: string;
    itemId: string;
  }>();
  const history = useHistory();
  const { state } = useLocation<GlobalLocationStateType>();
  const routes = useRoutes();
  const { t } = useTranslate();

  useDebouncedEffect(
    () => {
      if (isDatasetHookLoading) return;

      if (expandedRow) {
        const index = rows.findIndex(
          (row) => row[dataKey] === expandedRow[dataKey]
        );
        if (index === -1) return;

        setCurrentSelectedRowIndex(index);
        // Delete moves the detail onto another row — the URL must follow it.
        if (String(expandedRow[dataKey]) !== String(itemId)) {
          history.replace(buildDetailUrlForRow(expandedRow[dataKey]), {
            ...state,
          });
        }
        return;
      }

      // handle case when url opened from external source.
      const indexOfTargetRow = rows.findIndex(
        (row) => String(row[dataKey]) === String(itemId)
      );

      if (indexOfTargetRow !== -1) {
        setCurrentSelectedRowIndex(indexOfTargetRow);
        setExpandedRow(rows[indexOfTargetRow]);
        return;
      }

      // Last record deleted: nothing to fall back to, the empty-row effect
      // closes the detail — no bogus "row not found".
      if (!rows.length) return;

      setExpandedRow(rows[0]);
      setCurrentSelectedRowIndex(0);
      history.replace(routes.datasetDetail(datasetId, rows[0][dataKey]), {
        ...state,
      });
      toast(t('toasts.rowNotFound'));
    },
    [isDatasetHookLoading, rows],
    DETAIL_ROW_SYNC_DEBOUNCE_MS
  );
};
